<template>
  <v-container fluid class="sage-hr-container pa-0">
    <div :class="['sage-hr-header', 'px-4', 'py-3', 'd-flex', 'align-center', { 'sage-hr-header--react': sageHRMode === 'react' }]">
      <v-icon color="white" class="mr-2">mdi-robot-happy</v-icon>
      <div>
        <h1 class="text-h6 font-weight-bold white--text mb-0">
          {{ sageHRMode === 'react' ? 'Sage HR Advisor' : 'Sage HR' }}
        </h1>
        <span class="text-caption" style="color: rgba(255,255,255,0.7)">
          {{ sageHRMode === 'react' ? 'Multi-step reasoning with contextual search' : 'Conversational talent intelligence' }}
        </span>
      </div>
      <v-spacer></v-spacer>
      <v-chip
        small
        :color="sageHRMode === 'react' ? '#8b5cf6' : '#007AFF'"
        text-color="white"
        class="mr-2 sage-hr-plus-badge"
      >
        <v-icon small left>{{ sageHRMode === 'react' ? 'mdi-brain' : 'mdi-chat-processing' }}</v-icon>
        {{ sageHRMode === 'react' ? 'Advisor' : 'Standard' }}
      </v-chip>
      <v-btn
        v-if="hasMessages || isLoading"
        text
        small
        dark
        @click="startNewConversation"
      >
        <v-icon small left>mdi-plus</v-icon>
        {{ isLoading ? 'Stop & Reset' : 'New Conversation' }}
      </v-btn>
    </div>

    <div class="sage-hr-messages" ref="messageContainer">
      <div v-if="!hasMessages" class="welcome-section pa-6">
        <div class="text-center mb-6">
          <v-avatar size="64" color="primary" class="mb-3">
            <v-icon dark size="36">mdi-robot-happy</v-icon>
          </v-avatar>
          <h2 class="text-h5 font-weight-bold mb-2 theme-heading">
            Welcome to Sage HR
          </h2>
          <p class="text-body-2 grey--text text--darken-1" style="max-width: 480px; margin: 0 auto">
            I help you find talent, build teams, plan succession, and analyze skills
            across your organization. Just tell me what you need.
          </p>
        </div>

        <div class="d-flex flex-wrap justify-center">
          <v-chip
            v-for="chip in quickActions"
            :key="chip.label"
            outlined
            color="primary"
            class="ma-1 quick-chip"
            @click="sendQuickAction(chip.prompt)"
          >
            <v-icon small left>{{ chip.icon }}</v-icon>
            {{ chip.label }}
          </v-chip>
        </div>
      </div>

      <div v-else class="messages-list pa-4">
        <div
          v-for="(msg, idx) in messages"
          :key="idx"
          :class="['message-row', msg.role === 'user' ? 'user-row' : 'assistant-row']"
        >
          <div v-if="msg.role === 'assistant'" class="d-flex align-start">
            <v-avatar size="32" color="primary" class="mr-2 mt-1 flex-shrink-0">
              <v-icon dark small>mdi-robot-happy</v-icon>
            </v-avatar>
            <div class="message-content assistant-bubble">
              <div class="text-body-2" v-html="formatMessage(msg.content)"></div>

              <div v-if="msg.toolResults && msg.toolResults.length > 0" class="tool-results mt-3">
                <div v-for="(tr, trIdx) in msg.toolResults" :key="trIdx">
                  <div v-if="tr.tool === 'search_profiles' || tr.tool === 'jd_match' || tr.tool === 'succession_candidates'">
                    <div class="text-caption font-weight-bold mb-2 theme-subheading">
                      {{ getToolLabel(tr.tool) }}
                    </div>
                    <v-card
                      v-for="(person, pIdx) in getResultList(tr.result, msg.toolResults, trIdx)"
                      :key="pIdx"
                      outlined
                      class="mb-2 result-card"
                      @click="toggleExpandCard(msg, trIdx, pIdx)"
                    >
                      <v-card-text class="pa-3">
                        <div class="d-flex align-center justify-space-between">
                          <div>
                            <div class="font-weight-bold text-body-2">{{ person.name }}</div>
                            <div class="text-caption grey--text">
                              {{ person.title }}
                              <span v-if="person.company"> at {{ person.company }}</span>
                            </div>
                          </div>
                          <div class="d-flex align-center">
                            <v-chip
                              v-if="person.match_category && ['unknown','none','n/a','undefined','potential match'].indexOf(person.match_category.toLowerCase()) < 0"
                              x-small
                              outlined
                              :color="getCategoryColor(person.match_category)"
                              class="mr-1"
                              style="font-size: 10px"
                            >
                              {{ person.match_category }}
                            </v-chip>
                            <v-chip
                              v-if="person.score != null"
                              x-small
                              :color="getScoreColor(person.score)"
                              dark
                              class="mr-1"
                            >
                              {{ person.score }}%
                            </v-chip>
                          </div>
                        </div>
                        <div v-if="person.key_skills && person.key_skills.length" class="mt-1">
                          <v-chip
                            v-for="skill in person.key_skills.slice(0, 4)"
                            :key="skill"
                            x-small
                            outlined
                            class="mr-1 mt-1"
                          >
                            {{ skill }}
                          </v-chip>
                        </div>
                        <div v-if="person.strongest_signal" class="text-caption font-italic mt-1" style="color: #6B7280; line-height: 1.3">
                          {{ person.strongest_signal }}
                        </div>

                        <v-expand-transition>
                          <div v-if="isCardExpanded(msg, trIdx, pIdx)" class="expanded-detail mt-2 pt-2" style="border-top: 1px solid #E2E8F0">
                            <div class="d-flex flex-wrap" style="gap: 12px">
                              <div v-if="person.seniority" class="text-caption">
                                <span class="grey--text">Seniority:</span> <span class="font-weight-bold">{{ person.seniority }}</span>
                              </div>
                              <div v-if="person.functional_area" class="text-caption">
                                <span class="grey--text">Function:</span> <span class="font-weight-bold">{{ person.functional_area }}</span>
                              </div>
                            </div>
                            <div class="mt-2 d-flex" style="gap: 8px">
                              <v-btn x-small text color="primary" @click.stop="viewProfile(person)">
                                <v-icon x-small left>mdi-account-details</v-icon>
                                View Full Profile
                              </v-btn>
                              <v-btn x-small text color="primary" @click.stop="askAboutPerson(person)">
                                <v-icon x-small left>mdi-chat-question</v-icon>
                                Ask Sage
                              </v-btn>
                            </div>
                          </div>
                        </v-expand-transition>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div v-else-if="tr.tool === 'get_profile_detail'">
                    <div class="text-caption font-weight-bold mb-2 theme-subheading">
                      Profile Detail
                    </div>
                    <v-card outlined class="mb-2">
                      <v-card-text class="pa-3">
                        <div class="font-weight-bold text-body-1">{{ tr.result.name }}</div>
                        <div class="text-caption grey--text mb-2">
                          {{ tr.result.title }} at {{ tr.result.company }}
                        </div>
                        <div v-if="tr.result.summary" class="text-body-2 mb-2">
                          {{ tr.result.summary }}
                        </div>
                        <div v-if="tr.result.skills && tr.result.skills.length">
                          <div class="text-caption font-weight-bold mb-1">Skills</div>
                          <v-chip
                            v-for="s in tr.result.skills.slice(0, 10)"
                            :key="s.name"
                            x-small
                            outlined
                            class="mr-1 mb-1"
                          >
                            {{ s.name }}
                          </v-chip>
                        </div>
                        <v-btn
                          x-small
                          text
                          color="primary"
                          class="mt-2"
                          @click="viewProfile(tr.result)"
                        >
                          View Full Profile
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div v-else-if="tr.tool === 'team_builder'">
                    <div class="text-caption font-weight-bold mb-2 theme-subheading">
                      Team Composition
                    </div>
                    <v-card outlined class="mb-2">
                      <v-card-text class="pa-3">
                        <div class="text-caption grey--text mb-2">
                          {{ tr.result.objective }}
                        </div>
                        <div
                          v-for="(member, mIdx) in (tr.result.team_members || [])"
                          :key="mIdx"
                          class="d-flex align-center justify-space-between py-1 theme-divider"
                        >
                          <div>
                            <span class="font-weight-bold text-body-2">{{ member.name }}</span>
                            <span class="text-caption grey--text ml-1">{{ member.title }}</span>
                          </div>
                          <v-chip x-small :color="getScoreColor(member.score)" dark>
                            {{ member.score }}%
                          </v-chip>
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div v-else-if="tr.tool === 'interview_questions'">
                    <div class="text-caption font-weight-bold mb-2 theme-subheading">
                      Interview Questions for {{ tr.result.profile_name }}
                    </div>
                    <v-card outlined class="mb-2">
                      <v-card-text class="pa-3">
                        <div
                          v-for="(q, qIdx) in (tr.result.questions || [])"
                          :key="qIdx"
                          class="mb-2"
                        >
                          <div class="text-body-2">
                            <strong>{{ qIdx + 1 }}.</strong> {{ q.question }}
                          </div>
                          <v-chip x-small outlined class="mt-1">{{ q.category }}</v-chip>
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div v-else-if="tr.tool === 'talent_intelligence'">
                    <div class="text-caption font-weight-bold mb-2 theme-subheading">
                      Talent Intelligence
                    </div>
                    <v-card outlined class="mb-2">
                      <v-card-text class="pa-3">
                        <v-row dense>
                          <v-col cols="4">
                            <div class="text-center">
                              <div class="text-h6 font-weight-bold theme-subheading">
                                {{ tr.result.total_profiles }}
                              </div>
                              <div class="text-caption">Total Profiles</div>
                            </div>
                          </v-col>
                          <v-col cols="4">
                            <div class="text-center">
                              <div class="text-h6 font-weight-bold" style="color: #F59E0B">
                                {{ tr.result.avg_achievements }}
                              </div>
                              <div class="text-caption">Avg Achievements</div>
                            </div>
                          </v-col>
                          <v-col cols="4">
                            <div class="text-center">
                              <div class="text-h6 font-weight-bold" style="color: #10B981">
                                {{ (tr.result.health_metrics || {}).avg_skills_per_profile || 0 }}
                              </div>
                              <div class="text-caption">Avg Skills</div>
                            </div>
                          </v-col>
                        </v-row>
                        <div v-if="tr.result.top_skills" class="mt-3">
                          <div class="text-caption font-weight-bold mb-1">Top Skills</div>
                          <v-chip
                            v-for="ts in tr.result.top_skills.slice(0, 8)"
                            :key="ts.skill"
                            x-small
                            outlined
                            color="primary"
                            class="mr-1 mb-1"
                          >
                            {{ ts.skill }} ({{ ts.count }})
                          </v-chip>
                        </div>
                        <div v-if="tr.result.seniority_distribution && tr.result.seniority_distribution.length" class="mt-3">
                          <div class="text-caption font-weight-bold mb-1">Seniority Distribution</div>
                          <div
                            v-for="sd in tr.result.seniority_distribution.slice(0, 6)"
                            :key="sd.level"
                            class="d-flex align-center mb-1"
                            style="gap: 8px"
                          >
                            <span class="text-caption" style="min-width: 70px; text-align: right">{{ sd.level }}</span>
                            <div style="flex: 1; height: 14px; background: #E2E8F0; border-radius: 7px; overflow: hidden">
                              <div
                                :style="{
                                  width: getSeniorityPct(sd.count, tr.result.total_profiles) + '%',
                                  height: '100%',
                                  background: getSeniorityBarColor(sd.level),
                                  borderRadius: '7px',
                                  transition: 'width 0.6s ease'
                                }"
                              ></div>
                            </div>
                            <span class="text-caption font-weight-bold" style="min-width: 32px">{{ getSeniorityPct(sd.count, tr.result.total_profiles) }}%</span>
                          </div>
                        </div>
                        <div v-if="tr.result.notable_individuals && tr.result.notable_individuals.length" class="mt-3">
                          <div class="text-caption font-weight-bold mb-1">Notable Individuals</div>
                          <div
                            v-for="(ni, niIdx) in tr.result.notable_individuals"
                            :key="niIdx"
                            class="d-flex align-center justify-space-between py-1"
                            style="border-bottom: 1px solid #F0F0F0; cursor: pointer"
                            @click="viewProfile(ni)"
                          >
                            <div>
                              <span class="text-body-2 font-weight-bold">{{ ni.name }}</span>
                              <span class="text-caption grey--text ml-1">{{ ni.title }}</span>
                            </div>
                            <v-chip
                              x-small
                              :color="ni.flag === 'high_achiever' ? '#10B981' : '#F59E0B'"
                              dark
                              style="font-size: 9px"
                            >
                              {{ ni.flag === 'high_achiever' ? ni.achievements_count + ' achievements' : 'Needs attention' }}
                            </v-chip>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>

                  <div v-else-if="tr.tool === 'skills_gap_analysis'">
                    <div class="text-caption font-weight-bold mb-2 theme-subheading">
                      Skills Gap Analysis
                    </div>
                    <v-card outlined class="mb-2">
                      <v-card-text class="pa-3">
                        <div class="d-flex align-center justify-space-between mb-2">
                          <span class="font-weight-bold text-body-2">
                            {{ tr.result.profile_name }} → {{ tr.result.target_role }}
                          </span>
                          <v-chip
                            small
                            :color="getScoreColor(tr.result.readiness_score)"
                            dark
                          >
                            {{ tr.result.readiness_score }}% Ready
                          </v-chip>
                        </div>
                        <div v-if="tr.result.matched_skills && tr.result.matched_skills.length" class="mb-2">
                          <div class="text-caption font-weight-bold" style="color: #10B981">
                            Matched Skills
                          </div>
                          <v-chip
                            v-for="s in tr.result.matched_skills"
                            :key="s"
                            x-small
                            color="#10B981"
                            dark
                            class="mr-1 mt-1"
                          >
                            {{ s }}
                          </v-chip>
                        </div>
                        <div v-if="tr.result.gap_skills && tr.result.gap_skills.length">
                          <div class="text-caption font-weight-bold" style="color: #EF4444">
                            Gap Skills
                          </div>
                          <v-chip
                            v-for="s in tr.result.gap_skills"
                            :key="s"
                            x-small
                            outlined
                            color="#EF4444"
                            class="mr-1 mt-1"
                          >
                            {{ s }}
                          </v-chip>
                        </div>
                        <div class="text-caption grey--text mt-2">
                          {{ tr.result.recommendation }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="d-flex justify-end">
            <div class="message-content user-bubble">
              <div class="text-body-2 white--text">{{ msg.content }}</div>
            </div>
          </div>
        </div>

        <div v-if="isLoading" class="message-row assistant-row">
          <div class="d-flex align-start">
            <v-avatar size="32" color="primary" class="mr-2 mt-1 flex-shrink-0">
              <v-icon dark small>mdi-robot-happy</v-icon>
            </v-avatar>
            <div class="message-content assistant-bubble">
              <div class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
              <div v-if="elapsedSeconds > 2" class="text-caption mt-1" style="color: rgba(0,0,0,0.4); font-size: 11px;">
                {{ elapsedLabel }}
                ({{ elapsedSeconds }}s)
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="suggestedActions.length > 0 && !isLoading"
        class="suggested-actions px-4 pb-3"
      >
        <div class="text-caption grey--text text--darken-1 mb-2 d-flex align-center" style="gap: 4px">
          <v-icon x-small color="grey darken-1">mdi-lightbulb-on-outline</v-icon>
          Suggested next steps
        </div>
        <div class="d-flex flex-wrap" style="gap: 6px">
          <v-chip
            v-for="(action, aIdx) in suggestedActions"
            :key="aIdx"
            :outlined="action.action !== 'handoff_search'"
            :dark="action.action === 'handoff_search'"
            small
            :color="'primary'"
            :class="action.action === 'handoff_search' ? 'suggestion-chip handoff-chip' : 'suggestion-chip'"
            @click="handleSuggestionChip(action)"
          >
            <v-icon small left v-if="action.icon">{{ action.icon }}</v-icon>
            {{ action.label || action }}
          </v-chip>
        </div>
      </div>

      <div
        v-if="featureNavChips.length > 0 && !isLoading"
        class="feature-nav-chips px-4 pb-3"
      >
        <div class="text-caption grey--text text--darken-1 mb-2 d-flex align-center" style="gap: 4px">
          <v-icon x-small color="grey darken-1">mdi-open-in-new</v-icon>
          Open in full view
        </div>
        <div class="d-flex flex-wrap" style="gap: 6px">
          <v-chip
            v-for="chip in featureNavChips"
            :key="chip.tool"
            outlined
            small
            color="#007AFF"
            class="suggestion-chip"
            @click="navigateToFeature(chip)"
          >
            <v-icon small left>{{ chip.icon }}</v-icon>
            {{ chip.label }}
          </v-chip>
        </div>
      </div>
    </div>

    <div class="sage-hr-input px-4 py-3">
      <div v-if="isRecording" class="recording-bar mb-2">
        <div class="recording-bar-left">
          <span class="recording-dot"></span>
          <span class="recording-label">Recording</span>
          <span class="recording-time">{{ formattedElapsed }}</span>
        </div>
        <div class="recording-bar-right">
          <v-btn small color="primary" depressed class="done-btn" @click="stopVoice">
            <v-icon small left>mdi-check</v-icon>
            Done
          </v-btn>
        </div>
      </div>
      <div v-if="isTranscribing" class="processing-bar mb-2">
        <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-2"></v-progress-circular>
        <span class="processing-label">Processing your audio...</span>
      </div>
      <div v-if="interimTranscript" class="text-caption grey--text font-italic mb-1 px-1">
        {{ interimTranscript }}
      </div>
      <div class="d-flex align-center">
        <v-text-field
          v-model="userMessage"
          placeholder="Ask Sage anything about your talent..."
          outlined
          dense
          hide-details
          color="primary"
          class="mr-2"
          @keyup.enter="sendMessage"
          :disabled="isLoading || isRecording"
        ></v-text-field>
        <v-btn
          icon
          :color="isRecording ? 'error' : 'grey'"
          @click="toggleVoice"
          :disabled="isLoading || isTranscribing"
          :title="isRecording ? 'Stop recording' : 'Voice input'"
          class="mr-1"
          :class="{ 'voice-pulse': isRecording }"
        >
          <v-icon>mdi-microphone</v-icon>
        </v-btn>
        <v-btn
          color="primary"
          dark
          icon
          @click="sendMessage"
          :disabled="!userMessage.trim() || isLoading"
          :loading="isLoading"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
      <div class="text-caption grey--text text--lighten-1 mt-1 text-center">
        Sage uses AI to interpret your request, then evidence-based matching to find results.
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

const FEATURE_NAV_MAP = {
  search_profiles: { label: "View in Search", icon: "mdi-magnify", route: "IntelligenceFindTalent", query: { tab: "search" } },
  skills_gap_analysis: { label: "Open Skills Gap", icon: "mdi-chart-bar", route: "IntelligenceSkillsGap" },
  team_composition: { label: "Open Team Builder", icon: "mdi-account-group", route: "IntelligenceTeamBuilder" },
  succession_analysis: { label: "View Succession", icon: "mdi-arrow-up-bold", route: "IntelligenceSuccessionPlanning" },
  talent_insights: { label: "View Insights", icon: "mdi-lightbulb", route: "IntelligenceTalentInsights" },
};

export default {
  name: "IntelligenceSageHR",
  data: () => ({
    userMessage: "",
    voiceBaseText: "",
    voiceSessionFinals: [],
    voiceCurrentSessionFinal: "",
    isRecording: false,
    isTranscribing: false,
    interimTranscript: "",
    recognition: null,
    mediaRecorder: null,
    audioChunks: [],
    useBrowserSpeech: false,
    elapsedSeconds: 0,
    elapsedInterval: null,
    expandedCards: {},
    quickActions: [
      { label: "Find talent for a role", icon: "mdi-account-search", prompt: "I need to find candidates for a role" },
      { label: "Build a team", icon: "mdi-account-multiple-plus", prompt: "Help me build a team" },
      { label: "Succession planning", icon: "mdi-arrow-up-bold", prompt: "I need succession candidates" },
      { label: "Skills gap analysis", icon: "mdi-chart-bar", prompt: "Analyze skills gaps for a candidate" },
      { label: "Talent insights", icon: "mdi-lightbulb", prompt: "Show me talent intelligence for our organization" },
      { label: "Interview prep", icon: "mdi-comment-question", prompt: "Generate interview questions for a candidate" },
    ],
  }),
  computed: {
    ...mapState("intelligenceSageHR", [
      "messages",
      "isLoading",
      "excludedIds",
      "suggestedActions",
      "lastSearchContext",
      "hasError",
      "errorMessage",
    ]),
    ...mapGetters("intelligenceSageHR", ["hasMessages"]),
    sageHRMode() {
      return this.$store.state.intelligenceAdmin.sageHRMode;
    },
    featureNavChips() {
      var chips = [];
      var seen = {};
      for (var i = this.messages.length - 1; i >= 0; i--) {
        var msg = this.messages[i];
        if (msg.role === "assistant" && msg.toolResults && msg.toolResults.length > 0) {
          for (var j = 0; j < msg.toolResults.length; j++) {
            var tool = msg.toolResults[j].tool || "";
            if (FEATURE_NAV_MAP[tool] && !seen[tool]) {
              seen[tool] = true;
              chips.push({ ...FEATURE_NAV_MAP[tool], tool: tool });
            }
          }
          break;
        }
      }
      return chips;
    },
    elapsedLabel() {
      if (this.elapsedSeconds < 10) return "Analyzing your request...";
      if (this.elapsedSeconds < 20) return "Searching talent pool...";
      return "Almost there...";
    },
    formattedElapsed() {
      var mins = Math.floor(this.elapsedSeconds / 60);
      var secs = this.elapsedSeconds % 60;
      return (mins < 10 ? "0" : "") + mins + ":" + (secs < 10 ? "0" : "") + secs;
    },
  },
  methods: {
    ...mapActions("intelligenceSageHR", [
      "sendMessage",
      "excludeProfile",
      "clearConversation",
    ]),
    async sendMsg() {
      const msg = this.userMessage.trim();
      if (!msg || this.isLoading) return;
      this.userMessage = "";
      await this.$store.dispatch("intelligenceSageHR/sendMessage", { message: msg, mode: this.sageHRMode });
      this.$nextTick(() => this.scrollToBottom());
    },
    sendMessage() {
      this.sendMsg();
    },
    sendQuickAction(prompt) {
      this.userMessage = prompt;
      this.sendMsg();
    },
    handleSuggestionChip(action) {
      if (typeof action === "string") {
        this.userMessage = action;
        this.sendMsg();
        return;
      }
      if (action.action === "handoff_search") {
        var ctx = action.searchContext || this.lastSearchContext || {};
        this.$router.push({
          name: "IntelligenceFindTalent",
          query: {
            tab: "search",
            q: ctx.query || "",
            from: "sage",
          },
        });
        return;
      }
      if (action.action === "view_profile" && action.profileId) {
        this.$router.push({
          name: "IntelligenceProfile",
          params: { profileId: action.profileId },
          query: { from: 'sagehr' },
        });
        return;
      }
      if (action.query) {
        this.userMessage = action.query;
        this.sendMsg();
      }
    },
    navigateToFeature(chip) {
      var routeObj = { name: chip.route };
      if (chip.query) routeObj.query = { ...chip.query, from: "sage" };
      else routeObj.query = { from: "sage" };
      this.$router.push(routeObj);
    },
    startNewConversation() {
      this.clearConversation();
      this.expandedCards = {};
      this.userMessage = "";
    },
    viewProfile(person) {
      const id = person.profile_id || person.profileId;
      if (id) {
        this.$router.push({
          name: "IntelligenceProfile",
          params: { profileId: id },
          query: { from: 'sagehr' },
        });
      }
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
    formatMessage(content) {
      if (!content) return "";
      return content
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    },
    getToolLabel(tool) {
      const labels = {
        search_profiles: "Search Results",
        jd_match: "JD Match Results",
        succession_candidates: "Succession Candidates",
        get_profile_detail: "Profile Detail",
        team_builder: "Team Composition",
        interview_questions: "Interview Questions",
        talent_intelligence: "Talent Intelligence",
        skills_gap_analysis: "Skills Gap Analysis",
      };
      return labels[tool] || tool;
    },
    getResultList(result, allToolResults, currentIndex) {
      if (!result) return [];
      const list = result.results || result.candidates || result.team_members || [];
      if (!allToolResults || currentIndex === 0) return list;
      const seenIds = new Set();
      for (let i = 0; i < currentIndex; i++) {
        const prev = allToolResults[i]?.result;
        if (!prev) continue;
        const prevList = prev.results || prev.candidates || prev.team_members || [];
        prevList.forEach(p => {
          if (p.profile_id) seenIds.add(p.profile_id.toLowerCase());
          if (p.name) seenIds.add(p.name.toLowerCase());
        });
      }
      return list.filter(p => {
        const id = (p.profile_id || "").toLowerCase();
        const name = (p.name || "").toLowerCase();
        return (!id || !seenIds.has(id)) && (!name || !seenIds.has(name));
      });
    },
    getScoreColor(score) {
      if (score >= 70) return "#10B981";
      if (score >= 40) return "#F59E0B";
      return "#EF4444";
    },
    getCategoryColor(category) {
      if (!category) return "grey";
      var cat = category.toLowerCase();
      if (cat.indexOf("elite") >= 0) return "#7C3AED";
      if (cat.indexOf("proven") >= 0 || cat.indexOf("strong") >= 0) return "#10B981";
      if (cat.indexOf("mention") >= 0 || cat.indexOf("notable") >= 0) return "#F59E0B";
      return "#94A3B8";
    },
    getSeniorityPct(count, total) {
      if (!total || total === 0) return 0;
      return Math.round((count / total) * 100);
    },
    getSeniorityBarColor(level) {
      if (!level) return "#94A3B8";
      var l = level.toLowerCase();
      if (l.indexOf("c-suite") >= 0 || l.indexOf("executive") >= 0) return "#7C3AED";
      if (l.indexOf("vp") >= 0) return "#6366F1";
      if (l.indexOf("director") >= 0) return "#3B82F6";
      if (l.indexOf("senior") >= 0) return "#0EA5E9";
      if (l.indexOf("manager") >= 0) return "#10B981";
      if (l.indexOf("mid") >= 0) return "#F59E0B";
      return "#94A3B8";
    },
    toggleExpandCard(msg, trIdx, pIdx) {
      var msgIdx = this.messages.indexOf(msg);
      var key = msgIdx + "_" + trIdx + "_" + pIdx;
      var current = this.expandedCards[key] || false;
      this.$set(this.expandedCards, key, !current);
    },
    isCardExpanded(msg, trIdx, pIdx) {
      var msgIdx = this.messages.indexOf(msg);
      var key = msgIdx + "_" + trIdx + "_" + pIdx;
      return !!this.expandedCards[key];
    },
    askAboutPerson(person) {
      this.userMessage = "Tell me more about " + person.name;
      this.sendMsg();
    },
    toggleVoice() {
      if (this.isRecording) {
        this.stopVoice();
      } else {
        this.startVoice();
      }
    },
    startVoice() {
      var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.useBrowserSpeech = !!SpeechRecognition;
      if (this.useBrowserSpeech) {
        this.startBrowserSpeech(SpeechRecognition);
      } else {
        this.startFallbackRecording();
      }
    },
    startBrowserSpeech(SpeechRecognition) {
      var self = this;
      this.voiceBaseText = this.userMessage;
      this.voiceSessionFinals = [];
      this.voiceCurrentSessionFinal = "";
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = "en-US";
      this.recognition.onresult = function(event) {
        var interim = "";
        var sessionFinal = "";
        for (var i = event.resultIndex; i < event.results.length; i++) {
          var transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            sessionFinal += transcript;
          } else {
            interim += transcript;
          }
        }
        if (sessionFinal) {
          self.voiceSessionFinals.push(sessionFinal);
          self.voiceCurrentSessionFinal = "";
        } else {
          self.voiceCurrentSessionFinal = interim;
        }
        var allFinals = self.voiceSessionFinals.join(" ");
        var base = self.voiceBaseText;
        var needsSpace = base.trim().length > 0 && !base.endsWith(" ");
        self.userMessage = base + (needsSpace && allFinals ? " " : "") + allFinals + (allFinals && interim ? " " : "") + interim;
        self.interimTranscript = interim;
      };
      this.recognition.onerror = function(event) {
        console.error("Speech recognition error:", event.error);
        self.isRecording = false;
        self.interimTranscript = "";
        self.stopElapsed();
      };
      this.recognition.onend = function() {
        if (self.isRecording) {
          try { self.recognition.start(); } catch (e) {
            self.isRecording = false;
            self.stopElapsed();
          }
        }
        self.interimTranscript = "";
      };
      try {
        this.recognition.start();
        this.isRecording = true;
        this.startElapsed();
      } catch (e) {
        console.error("Failed to start recognition:", e);
      }
    },
    startFallbackRecording() {
      var self = this;
      navigator.mediaDevices.getUserMedia({ audio: true }).then(function(stream) {
        self.audioChunks = [];
        var mimeType = "audio/webm";
        var types = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
        for (var i = 0; i < types.length; i++) {
          if (MediaRecorder.isTypeSupported(types[i])) { mimeType = types[i]; break; }
        }
        self.mediaRecorder = new MediaRecorder(stream, { mimeType: mimeType });
        self.mediaRecorder.ondataavailable = function(event) {
          if (event.data.size > 0) self.audioChunks.push(event.data);
        };
        self.mediaRecorder.onstop = function() {
          stream.getTracks().forEach(function(t) { t.stop(); });
          self.transcribeAudio(mimeType);
        };
        self.mediaRecorder.start(100);
        self.isRecording = true;
        self.startElapsed();
      }).catch(function(e) {
        console.error("Microphone access denied:", e);
      });
    },
    stopVoice() {
      this.stopElapsed();
      if (this.useBrowserSpeech && this.recognition) {
        try { this.recognition.stop(); } catch (e) { /* ignore */ }
        this.recognition = null;
      } else if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
        this.mediaRecorder.stop();
      }
      this.isRecording = false;
      this.interimTranscript = "";
    },
    transcribeAudio(mimeType) {
      if (this.audioChunks.length === 0) return;
      this.isTranscribing = true;
      var self = this;
      var audioBlob = new Blob(this.audioChunks, { type: mimeType });
      var reader = new FileReader();
      reader.onloadend = function() {
        var base64 = reader.result.split(",")[1];
        fetch("/api/Transcribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ audio: base64, mimeType: mimeType })
        }).then(function(r) { return r.json(); }).then(function(result) {
          if (result.success && result.transcript) {
            var needsSpace = self.userMessage.trim().length > 0 && !self.userMessage.endsWith(" ");
            self.userMessage = self.userMessage + (needsSpace ? " " : "") + result.transcript;
          }
        }).catch(function(e) {
          console.error("Transcription error:", e);
        }).finally(function() {
          self.isTranscribing = false;
          self.audioChunks = [];
        });
      };
      reader.readAsDataURL(audioBlob);
    },
    startElapsed() {
      this.elapsedSeconds = 0;
      var self = this;
      this.elapsedInterval = setInterval(function() { self.elapsedSeconds++; }, 1000);
    },
    stopElapsed() {
      if (this.elapsedInterval) {
        clearInterval(this.elapsedInterval);
        this.elapsedInterval = null;
      }
      this.elapsedSeconds = 0;
    },
    async loadSageHRMode() {
      await this.$store.dispatch("intelligenceAdmin/loadSageHRMode");
    },
  },
  mounted() {
    var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.useBrowserSpeech = !!SpeechRecognition;
    var prefill = this.$route && this.$route.query && this.$route.query.prefill;
    if (prefill && typeof prefill === 'string' && prefill.trim()) {
      this.userMessage = prefill.trim();
    }
    this.loadSageHRMode();
  },
  beforeDestroy() {
    this.stopElapsed();
    if (this.recognition) {
      try { this.recognition.stop(); } catch (e) { /* ignore */ }
    }
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.stop();
    }
  },
  watch: {
    messages: {
      handler() {
        this.$nextTick(() => this.scrollToBottom());
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.sage-hr-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 106px);
  max-height: calc(100vh - 106px);
}

.sage-hr-header {
  background: linear-gradient(135deg, #1E3A5F 0%, #2C5282 100%);
  flex-shrink: 0;
  transition: background 0.3s ease;
}

.sage-hr-header--react {
  background: linear-gradient(135deg, #4C1D95 0%, #7C3AED 50%, #8B5CF6 100%);
}

.sage-hr-toggle {
  flex: 0 0 auto;
  max-width: 80px;
}

.sage-hr-toggle >>> .v-label {
  font-size: 12px;
  font-weight: 600;
}

.sage-hr-plus-badge {
  font-weight: 600;
  letter-spacing: 0.5px;
  animation: sagePulse 2s ease-in-out infinite;
}

@keyframes sagePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { box-shadow: 0 0 8px 2px rgba(139, 92, 246, 0.3); }
}

.sage-hr-messages {
  flex: 1;
  overflow-y: auto;
  background: #F7F8FA;
}

.sage-hr-input {
  flex-shrink: 0;
  background: white;
  border-top: 1px solid #E2E8F0;
}

.welcome-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.message-row {
  margin-bottom: 16px;
}

.user-row {
  display: flex;
  justify-content: flex-end;
}

.message-content {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  word-break: break-word;
}

.user-bubble {
  background: #007AFF;
  border-bottom-right-radius: 4px;
}

.assistant-bubble {
  background: white;
  border: 1px solid #E2E8F0;
  border-bottom-left-radius: 4px;
}

.result-card {
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s ease;
}

.result-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.expanded-detail {
  animation: expandIn 0.2s ease;
}

@keyframes expandIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.quick-chip {
  cursor: pointer;
  transition: background-color 0.2s;
}

.quick-chip:hover {
  background-color: rgba(0, 122, 255, 0.08);
}

.suggestion-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.suggestion-chip:hover {
  background-color: rgba(0, 122, 255, 0.08);
  transform: translateY(-1px);
}

.handoff-chip {
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0, 122, 255, 0.3);
}

.handoff-chip:hover {
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.4);
}

.suggested-actions {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #007AFF;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: 0s; }
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0.4); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

.voice-pulse {
  animation: pulse-ring 1.2s ease-in-out infinite;
}

@keyframes pulse-ring {
  0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
}

.recording-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #FFF3E0;
  border: 1px solid #FFB74D;
  border-radius: 8px;
  padding: 6px 12px;
}

.recording-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.recording-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #F44336;
  animation: blink-dot 1s ease-in-out infinite;
}

@keyframes blink-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.recording-label {
  font-size: 13px;
  font-weight: 600;
  color: #E65100;
}

.recording-time {
  font-size: 12px;
  font-family: monospace;
  color: #BF360C;
  background: rgba(191, 54, 12, 0.08);
  padding: 1px 6px;
  border-radius: 4px;
}

.recording-bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.done-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
}

.processing-bar {
  display: flex;
  align-items: center;
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 8px;
  padding: 6px 12px;
}

.processing-label {
  font-size: 13px;
  color: #1565C0;
  font-weight: 500;
}
</style>

<style>
.theme--dark .sage-hr-messages {
  background: #1a1a2e;
}
.theme--dark .sage-hr-input {
  background: #1e1e2e;
  border-top-color: #444;
}
.theme--dark .assistant-bubble {
  background: #2a2a3e;
  border-color: #444;
}
.theme--dark .suggested-actions {
  border-top-color: #444;
}
.theme--dark .recording-bar {
  background: #3e2a00;
  border-color: #BF8040;
}
.theme--dark .processing-bar {
  background: #1a2e3e;
  border-color: #42A5F5;
}
.theme--dark .processing-label {
  color: #90CAF9;
}
.theme--dark .recording-label {
  color: #FFB74D;
}
.theme--dark .recording-time {
  color: #FFB74D;
  background: rgba(255, 183, 77, 0.12);
}
.theme--dark .expanded-detail {
  border-top-color: #424242 !important;
}
.theme--dark .result-card .font-italic {
  color: #9CA3AF !important;
}
</style>
